import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Tooltip,
  Grid,
  Paper,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ScienceIcon from "@mui/icons-material/Science";
import LabelIcon from "@mui/icons-material/Label";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CategoryIcon from "@mui/icons-material/Category";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1060, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    borderWidth: "1.5px",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const StyledSelect = styled("select")(() => ({
  width: "100%",
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  appearance: "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora', sans-serif",
}));

const SubjectCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  padding: "20px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  animation: `${cardEntrance} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:hover": {
    boxShadow: "0 6px 20px rgba(37,99,235,0.09), 0 10px 32px rgba(0,0,0,0.07)",
    transform: "translateY(-2px)",
    borderColor: alpha("#2563EB", 0.2),
  },
}));

const SubjectIconBox = styled(Box)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  flexShrink: 0,
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 10px rgba(37,99,235,0.25)",
  marginBottom: 14,
}));

const NavActionBtn = styled(Box, {
  shouldForwardProp: (p) => p !== "btnvariant",
})(({ theme, btnvariant }) => {
  const map = {
    practice: { color: "#2563EB", bg: alpha("#2563EB", 0.07), hbg: "#2563EB" },
    exam: { color: "#059669", bg: alpha("#059669", 0.07), hbg: "#059669" },
    edit: { color: "#C4922A", bg: alpha("#C4922A", 0.08), hbg: "#C4922A" },
  };
  const s = map[btnvariant] || map.practice;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    cursor: "pointer",
    padding: "7px 12px",
    borderRadius: 9,
    border: `1px solid ${alpha(s.color, 0.2)}`,
    background: s.bg,
    flex: 1,
    transition: "all 0.18s ease",
    "&:hover": {
      background: s.hbg,
      borderColor: s.hbg,
      "& .btn-label": { color: "#fff" },
      "& .btn-icon": { color: "#fff" },
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.28)}`,
    },
  };
});

const ManageCatBtn = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: "9px 20px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  border: `1px solid ${alpha("#000", 0.12)}`,
  color: "text.secondary",
  background: "#fff",
  fontFamily: "'Sora', sans-serif",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha("#2563EB", 0.05),
    borderColor: alpha("#2563EB", 0.3),
    color: "#2563EB",
  },
}));

const Spinner = styled(Box)(() => ({
  width: 32,
  height: 32,
  border: "3px solid rgba(37,99,235,0.15)",
  borderTop: "3px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminSubjectPage() {
  const navigate = useNavigate();
  const { examId } = useParams();

  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [objTime, setObjTime] = useState("00:00:00");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchSubjects = useCallback(() => {
    setFetching(true);
    fetch(`${API_URL}/admin/subjects/${examId}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setSubjects(data);
        setFetching(false);
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to load subjects.");
        setSubjects([]);
        setFetching(false);
      });
  }, [examId]);

  const fetchCategories = useCallback(() => {
    axios
      .get(`${API_URL}/admin/subjectcategory/`)
      .then((res) => setCategories(res.data))
      .catch((err) => console.error("Failed to load categories:", err));
  }, []);

  useEffect(() => {
    fetchSubjects();
    fetchCategories();
  }, [fetchSubjects, fetchCategories]);

  const resetForm = () => {
    setName("");
    setCategory("");
    setObjTime("00:00:00");
    setEditMode(false);
    setEditId(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert("Subject name is required.");
      return;
    }
    setLoading(true);
    const payload = {
      name: name.trim(),
      category: category || null,
      obj_time: objTime || null,
    };
    const method = editMode ? "PUT" : "POST";
    const url = editMode
      ? `${API_URL}/admin/subject/${editId}/`
      : `${API_URL}/admin/subjects/${examId}/`;
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (editMode)
        setSubjects((prev) => prev.map((s) => (s.id === editId ? data : s)));
      else setSubjects((prev) => [data, ...prev]);
      resetForm();
    } catch {
      alert(`Failed to ${editMode ? "update" : "add"} subject.`);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (subject) => {
    setEditMode(true);
    setEditId(subject.id);
    setName(subject.name);
    setCategory(subject.category || "");
    setObjTime(subject.obj_time || "00:00:00");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToTest = (subjectId) => navigate(`/admin-test/${subjectId}`);
  const navigateToExamTest = (subjectId) =>
    navigate(`/admin-exam-test/${subjectId}`);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editMode ? "Edit Subject" : "Manage Subjects"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Exam ID: <strong style={{ color: "#4B5563" }}>{examId}</strong>
              </Typography>
            </Box>

            <Box
              sx={{
                ml: "auto",
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <ManageCatBtn
                startIcon={
                  <CategoryIcon sx={{ fontSize: "15px !important" }} />
                }
                onClick={() => navigate("/admin-subject-category")}
              >
                Subject Categories
              </ManageCatBtn>
              <Chip
                label={editMode ? "Editing" : "New"}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  background: editMode
                    ? alpha("#C4922A", 0.12)
                    : alpha("#2563EB", 0.1),
                  color: editMode ? "#C4922A" : "#2563EB",
                  border: `1px solid ${
                    editMode ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                  }`,
                }}
              />
            </Box>
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>
              {editMode ? "Edit Subject" : "Add New Subject"}
            </CardLabel>

            <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
              {/* Subject Name */}
              <Box sx={{ flex: 2, minWidth: 200 }}>
                <FieldLabel>Subject Name *</FieldLabel>
                <StyledInput
                  placeholder="e.g. Mathematics, English Language…"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              {/* Category */}
              <Box sx={{ flex: 1, minWidth: 160, position: "relative" }}>
                <FieldLabel>Category (optional)</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <LabelIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                  <StyledSelect
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  >
                    <option value="">None</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </StyledSelect>
                  <ArrowForwardIosIcon
                    sx={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%) rotate(90deg)",
                      fontSize: 11,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </Box>

              {/* Obj Time */}
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <FieldLabel>Objective Time Limit</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <AccessTimeIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    placeholder="hh:mm:ss"
                    value={objTime}
                    onChange={(e) => setObjTime(e.target.value)}
                    style={{ paddingLeft: 36 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.5,
                  }}
                >
                  Leave blank if no time limit
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={
                  editMode ? (
                    <EditIcon sx={{ fontSize: "16px !important" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "16px !important" }} />
                  )
                }
                disabled={loading || !name.trim()}
                onClick={handleSubmit}
              >
                {loading
                  ? "Saving…"
                  : editMode
                  ? "Update Subject"
                  : "Add Subject"}
              </PrimaryBtn>
              {editMode && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Subjects Grid ── */}
          <SectionCard delay={120}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>All Subjects</CardLabel>
              <Chip
                label={`${subjects.length} subject${
                  subjects.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.08),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.18)}`,
                }}
              />
            </Box>

            {fetching ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 5,
                  gap: 2,
                }}
              >
                <Spinner />
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Loading subjects…
                </Typography>
              </Box>
            ) : subjects.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <SchoolIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No subjects added yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to add your first subject
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {subjects.map((subject, i) => (
                  <Grid item xs={12} sm={6} md={4} key={subject.id}>
                    <SubjectCard itemdelay={i * 55}>
                      {/* Icon + Name */}
                      <SubjectIconBox>
                        <SchoolIcon sx={{ fontSize: 22, color: "#fff" }} />
                      </SubjectIconBox>

                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "text.primary",
                          fontFamily: "'DM Serif Display', serif",
                          mb: 0.6,
                          lineHeight: 1.25,
                        }}
                      >
                        {subject.name}
                      </Typography>

                      {/* Meta chips */}
                      <Stack
                        direction="row"
                        spacing={0.8}
                        flexWrap="wrap"
                        sx={{ mb: 1.5, gap: 0.6 }}
                      >
                        {subject.category_name && (
                          <Chip
                            icon={
                              <LabelIcon sx={{ fontSize: "11px !important" }} />
                            }
                            label={subject.category_name}
                            size="small"
                            sx={{
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 700,
                              fontSize: 10,
                              background: alpha("#7C3AED", 0.08),
                              color: "#7C3AED",
                              border: `1px solid ${alpha("#7C3AED", 0.2)}`,
                              "& .MuiChip-icon": { color: "#7C3AED" },
                            }}
                          />
                        )}
                        {subject.obj_time && (
                          <Chip
                            icon={
                              <AccessTimeIcon
                                sx={{ fontSize: "11px !important" }}
                              />
                            }
                            label={subject.obj_time}
                            size="small"
                            sx={{
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 700,
                              fontSize: 10,
                              background: alpha("#C4922A", 0.08),
                              color: "#C4922A",
                              border: `1px solid ${alpha("#C4922A", 0.2)}`,
                              "& .MuiChip-icon": { color: "#C4922A" },
                            }}
                          />
                        )}
                      </Stack>

                      {/* Spacer */}
                      <Box sx={{ flex: 1 }} />

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={0.8} sx={{ mt: 1.5 }}>
                        <Tooltip title="Practice Tests" arrow>
                          <NavActionBtn
                            btnvariant="practice"
                            onClick={() => navigateToTest(subject.id)}
                          >
                            <MenuBookIcon
                              className="btn-icon"
                              sx={{
                                fontSize: 13,
                                color: "#2563EB",
                                transition: "color 0.18s",
                              }}
                            />
                            <Typography
                              className="btn-label"
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#2563EB",
                                fontFamily: "'Sora', sans-serif",
                                transition: "color 0.18s",
                              }}
                            >
                              Practice
                            </Typography>
                          </NavActionBtn>
                        </Tooltip>

                        <Tooltip title="Main Exam Tests" arrow>
                          <NavActionBtn
                            btnvariant="exam"
                            onClick={() => navigateToExamTest(subject.id)}
                          >
                            <ScienceIcon
                              className="btn-icon"
                              sx={{
                                fontSize: 13,
                                color: "#059669",
                                transition: "color 0.18s",
                              }}
                            />
                            <Typography
                              className="btn-label"
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#059669",
                                fontFamily: "'Sora', sans-serif",
                                transition: "color 0.18s",
                              }}
                            >
                              Exam
                            </Typography>
                          </NavActionBtn>
                        </Tooltip>

                        <Tooltip title="Edit Subject" arrow>
                          <NavActionBtn
                            btnvariant="edit"
                            onClick={() => startEdit(subject)}
                          >
                            <EditIcon
                              className="btn-icon"
                              sx={{
                                fontSize: 13,
                                color: "#C4922A",
                                transition: "color 0.18s",
                              }}
                            />
                            <Typography
                              className="btn-label"
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#C4922A",
                                fontFamily: "'Sora', sans-serif",
                                transition: "color 0.18s",
                              }}
                            >
                              Edit
                            </Typography>
                          </NavActionBtn>
                        </Tooltip>
                      </Stack>
                    </SubjectCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
